import { FiCalendar, FiMapPin } from "react-icons/fi";
import SectionHeading from "../../components/SectionHeading";
import Reveal from "../../components/Reveal";
import TiltCard from "../../components/TiltCard";

/**
 * Career / job experience — a vertical timeline of the companies I've worked
 * with, each with the projects I shipped under it. Monogram nodes keep their
 * colour accent; everything else follows the light/dark theme tokens.
 */
const EXPERIENCE = [
  {
    company: "K53 Technology Solution",
    role: "Software Engineer & Team Lead",
    period: "Apr 2020 — Present",
    location: "Dhaka, Bangladesh",
    mono: "K53",
    accent: "from-aurora-violet to-aurora-indigo",
    work: [
      {
        name: "Allianz — Identity Governance",
        desc: "Enterprise IAM as a One Identity expert — RBAC and user-lifecycle workflows, with Angular apps built on top (OAuth2 / OIDC / SAML).",
      },
      {
        name: "Squarebear",
        desc: "Canvas-based project-management & delivery platform (Spring Boot + React Flow), secured with Keycloak and ForgeRock OpenAM.",
      },
      {
        name: "WorkCover Queensland",
        desc: "Config-driven microservices for an Australian Government client — Kafka, PostgreSQL, services generated from Swagger definitions.",
      },
      {
        name: "Nettverk",
        desc: "Bi-directional data-sync service across SailPoint, Xero and WooCommerce behind a custom interface.",
      },
      {
        name: "OneTeam",
        desc: "Kanban-style project-management platform on a microservices backend, with Jest coverage across frontend and backend.",
      },
      {
        name: "RichBengali",
        desc: "Cross-platform Flutter dating & live-call app — Agora RTC calls, native CallKit/ConnectionService, per-minute earnings & payouts.",
      },
      {
        name: "Forge",
        desc: "Product & project platform; rewrote the backend from Python/FastAPI to Rust (Axum), with Keycloak auth and real-time collaboration.",
      },
      {
        name: "BackpackLink",
        desc: "Cross-platform travel-eSIM marketplace (Next.js + Flutter + Express/Prisma), Stripe checkout and self-hosted CI/CD.",
      },
    ],
  },
  {
    company: "FlashSites GmbH",
    role: "Software Engineer",
    period: "Jul 2024 — Present",
    location: "Germany · Remote",
    mono: "FS",
    accent: "from-aurora-cyan to-aurora-teal",
    work: [
      {
        name: "Email Automation",
        desc: "AI-assisted email automation system in Python & React — NLP content generation, template management, scheduling and delivery tracking.",
      },
      {
        name: "CDN Control Plane",
        desc: "Multi-region CDN control plane: exactly-once config/cert distribution over Redis Streams, Vault-encrypted keys and gRPC / Envoy edge delivery.",
      },
    ],
  },
  {
    company: "Devs On Steroids",
    role: "DevOps Engineer (Contract)",
    period: "Dec 2025 — Feb 2026",
    location: "Remote",
    mono: "DS",
    accent: "from-aurora-pink to-aurora-rose",
    work: [
      {
        name: "Hospital Management System",
        desc: "Scalable microservices platform (NestJS, FastAPI, MySQL) on Azure AKS with HAProxy load balancing and Prometheus/Grafana monitoring.",
      },
    ],
  },
  {
    company: "Triosoft Ltd",
    role: "Software Engineer",
    period: "Mar 2020 — Mar 2021",
    location: "Dhaka, Bangladesh",
    mono: "TS",
    accent: "from-aurora-indigo to-aurora-purple",
    work: [
      {
        name: "Antooba Shop",
        desc: "ERP with Point-of-Sale, Inventory and HRM modules (Laravel API + React).",
      },
      {
        name: "EFG Tailor",
        desc: "E-commerce platform with a custom clothing-personalization tool (Node.js, Next.js, Redis).",
      },
    ],
  },
  {
    company: "iVoyant",
    role: "Frontend Developer (Contract)",
    period: "May 2023 — Jul 2023",
    location: "India · Remote",
    mono: "iV",
    accent: "from-aurora-purple to-aurora-pink",
    work: [
      {
        name: "Decision Table",
        desc: "Reusable, fully configurable decision-table component (When / Then / Annotations logic) in Next.js + TypeScript.",
      },
    ],
  },
  {
    company: "SageSEO",
    role: "Frontend Developer (Contract)",
    period: "Aug 2022 — Oct 2022",
    location: "Australia · Remote",
    mono: "SS",
    accent: "from-aurora-teal to-aurora-cyan",
    work: [
      {
        name: "SageSEO Platform",
        desc: "ChatGPT-powered AI content & keyword-generation SaaS (Vue.js / Nuxt.js, OpenAI) — AI integration layer plus admin and client UIs.",
      },
    ],
  },
];

const Career = () => {
  return (
    <section id="experience" className="relative px-6 py-28 md:py-36">
      <div className="container mx-auto max-w-4xl">
        <SectionHeading
          eyebrow="Experience"
          title="Where I've Worked"
          subtitle="Six years across product companies and contracts — here's who I built for, and what I shipped under each."
        />

        <div className="relative">
          {/* timeline spine */}
          <div
            className="absolute left-6 top-3 bottom-3 w-px bg-hairline md:left-8"
            aria-hidden="true"
          />

          <div className="flex flex-col gap-10">
            {EXPERIENCE.map((exp, i) => (
              <Reveal key={exp.company} direction="up" delay={i * 0.05}>
                <div className="relative pl-16 md:pl-24">
                  {/* monogram node (keeps its colour accent) */}
                  <span
                    className={`absolute left-0 top-0 flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br ${exp.accent} text-sm font-bold text-white shadow-glow md:h-16 md:w-16 md:text-base`}
                  >
                    {exp.mono}
                  </span>

                  <TiltCard className="glass rounded-3xl p-6 md:p-7" max={4}>
                    <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
                      <div>
                        <h3 className="text-lg font-bold text-content md:text-xl">
                          {exp.company}
                        </h3>
                        <p className="mt-0.5 text-sm font-medium text-muted">
                          {exp.role}
                        </p>
                      </div>
                      <div className="flex flex-col gap-1 text-xs text-faint sm:items-end">
                        <span className="inline-flex items-center gap-1.5">
                          <FiCalendar className="h-3.5 w-3.5" /> {exp.period}
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                          <FiMapPin className="h-3.5 w-3.5" /> {exp.location}
                        </span>
                      </div>
                    </div>

                    <ul className="mt-6 flex flex-col gap-4 border-t border-hairline pt-5">
                      {exp.work.map((w) => (
                        <li key={w.name} className="flex gap-3">
                          <span
                            className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-content"
                            aria-hidden="true"
                          />
                          <div>
                            <p className="text-sm font-semibold text-content">
                              {w.name}
                            </p>
                            <p className="mt-0.5 text-sm leading-relaxed text-muted">
                              {w.desc}
                            </p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </TiltCard>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Career;
